import { useEffect, useMemo, useState } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  computeTC6PrimaryReference,
  getTc6BestAttempt,
  getTc6DistanceSeries,
  getTc6Attempts,
  getTc6VitalsSeries,
  recalculateTc6Payload,
  resolveTC6Interpretation,
  summarizeTc6Result,
  type Tc6AttemptPayload,
  type Tc6ChartImages,
  type Tc6ResultPayload,
} from '../tc6/tc6Domain';
import { TC6Comparison } from '../../../types/TC6Comparison';

export const TC6_NO_COMPARISON = '__none__';

export const getTC6SeverityLabel = (percentPredicted?: number | null): string => {
  if (percentPredicted == null) return 'Capacidade não comparada';
  if (percentPredicted >= 90) return 'Capacidade preservada';
  if (percentPredicted >= 75) return 'Redução leve';
  if (percentPredicted >= 60) return 'Redução moderada';
  return 'Redução grave';
};

export const getTC6SeverityTone = (percentPredicted?: number | null): string => {
  if (percentPredicted == null) return 'bg-slate-100 text-slate-700';
  if (percentPredicted >= 90) return 'bg-emerald-100 text-emerald-700';
  if (percentPredicted >= 75) return 'bg-lime-100 text-lime-700';
  if (percentPredicted >= 60) return 'bg-amber-100 text-amber-700';
  return 'bg-rose-100 text-rose-700';
};

interface TC6ResultRendererProps {
  payload: Tc6ResultPayload | null | undefined;
  assessmentDate?: string | null;
  chartImages?: Tc6ChartImages | null;
  comparisonHistory?: Tc6ComparisonOption[];
  selectedComparisonId?: string;
  onComparisonChange?: (value: string) => void;
}

export interface Tc6ComparisonOption {
  id: string;
  label: string;
  assessmentDate?: string | null;
  payload: Tc6ResultPayload | null | undefined;
}

export type Tc6ResolvedComparison = {
  attempt: Tc6AttemptPayload;
  label: string;
  assessmentDate?: string | null;
  referenceKey: Tc6ResultPayload['referenceEquation'];
};

const SummaryMetric = ({ label, value, note }: { label: string; value: string; note?: string | null }) => (
  <div className="rounded-[24px] border border-slate-100 bg-white p-5 shadow-sm">
    <div className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">{label}</div>
    <div className="mt-2 text-3xl font-black text-slate-900">{value}</div>
    {note ? <div className="mt-2 text-sm text-slate-500">{note}</div> : null}
  </div>
);

export function TC6DistanceChartSurface({
  attempt,
  referenceKey,
  comparisonAttempt,
  disableAnimation = false,
}: {
  attempt: Tc6AttemptPayload;
  referenceKey: Tc6ResultPayload['referenceEquation'];
  comparisonAttempt?: Tc6AttemptPayload | null;
  disableAnimation?: boolean;
}) {
  const distanceSeries = getTc6DistanceSeries(attempt, referenceKey);
  const comparisonSeries = comparisonAttempt ? getTc6DistanceSeries(comparisonAttempt) : null;
  const chartData = distanceSeries.map((entry, index) => ({
    ...entry,
    comparisonDistance: comparisonSeries?.[index]?.realDistance ?? null,
  }));

  return (
    <div className="h-72 rounded-2xl border border-slate-100 bg-white p-2">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData} margin={{ top: 10, right: 18, left: 2, bottom: 12 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="minute" allowDecimals={false} />
          <YAxis />
          <Tooltip />
          <Legend />
          {comparisonAttempt ? (
            <Line
              type="monotone"
              dataKey="comparisonDistance"
              name="Distância histórica (m)"
              stroke="#94a3b8"
              strokeWidth={2}
              strokeDasharray="6 4"
              dot={false}
              connectNulls
              isAnimationActive={!disableAnimation}
            />
          ) : null}
          <Line
            type="monotone"
            dataKey="realDistance"
            name="Distância real (m)"
            stroke="#2563eb"
            strokeWidth={3}
            dot
            connectNulls
            isAnimationActive={!disableAnimation}
          />
          <Line
            type="monotone"
            dataKey="predictedDistance"
            name="Distância prevista (m)"
            stroke="#f97316"
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={false}
            connectNulls
            isAnimationActive={!disableAnimation}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export function TC6VitalsChartSurface({
  attempt,
  comparisonAttempt,
  disableAnimation = false,
}: {
  attempt: Tc6AttemptPayload;
  comparisonAttempt?: Tc6AttemptPayload | null;
  disableAnimation?: boolean;
}) {
  const vitalsSeries = getTc6VitalsSeries(attempt);
  const comparisonSeries = comparisonAttempt ? getTc6VitalsSeries(comparisonAttempt) : null;
  const chartData = vitalsSeries.map((entry, index) => ({
    ...entry,
    comparisonHr: comparisonSeries?.[index]?.hr ?? null,
    comparisonSpo2: comparisonSeries?.[index]?.spo2 ?? null,
  }));

  return (
    <div className="h-72 rounded-2xl border border-slate-100 bg-white p-2">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData} margin={{ top: 10, right: 18, left: 2, bottom: 12 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="minute" allowDecimals={false} />
          <YAxis yAxisId="left" stroke="#2563eb" />
          <YAxis yAxisId="right" orientation="right" stroke="#0f766e" domain={[70, 100]} />
          <Tooltip />
          <Legend />
          <ReferenceLine yAxisId="right" y={88} stroke="#ef4444" strokeDasharray="4 4" />
          {comparisonAttempt ? (
            <>
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="comparisonSpo2"
                name="SpO2 histórica"
                stroke="#64748b"
                strokeWidth={2}
                strokeDasharray="6 4"
                dot={false}
                connectNulls
                isAnimationActive={!disableAnimation}
              />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="comparisonHr"
                name="FC histórica"
                stroke="#94a3b8"
                strokeWidth={2}
                strokeDasharray="6 4"
                dot={false}
                connectNulls
                isAnimationActive={!disableAnimation}
              />
            </>
          ) : null}
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="spo2"
            name="SpO2 (%)"
            stroke="#0f766e"
            strokeWidth={3}
            dot
            connectNulls
            isAnimationActive={!disableAnimation}
          />
          <Line
            yAxisId="left"
            type="monotone"
            dataKey="hr"
            name="FC (bpm)"
            stroke="#2563eb"
            strokeWidth={3}
            dot
            connectNulls
            isAnimationActive={!disableAnimation}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function AttemptCharts({
  attemptKey,
  payload,
  chartImages,
  attemptLabel,
  comparison,
  assessmentDate,
}: {
  attemptKey: 'attempt1' | 'attempt2';
  payload: Tc6ResultPayload;
  chartImages?: Tc6ChartImages | null;
  attemptLabel: string;
  comparison?: Tc6ResolvedComparison | null;
  assessmentDate?: string | null;
}) {
  const attempt = payload?.[attemptKey];
  if (!attempt) return null;

  const selectedReferenceKey = payload.referenceEquation;
  const distanceImage = chartImages?.[`${attemptKey}_distance_payload` as keyof Tc6ChartImages] ?? null;
  const vitalsImage = chartImages?.[`${attemptKey}_vitals_payload` as keyof Tc6ChartImages] ?? null;
  const primaryReference = computeTC6PrimaryReference(attempt, selectedReferenceKey);
  const stopLines = attempt.stop_report?.intervals ?? [];
  const showStoredCharts = !comparison;

  return (
    <div className="space-y-5 rounded-[28px] border border-slate-100 bg-white p-6 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h4 className="text-xl font-black tracking-tight text-slate-900">{attemptLabel}</h4>
          <p className="mt-1 text-sm text-slate-500">
            {attempt.total_distance_m} m
            {primaryReference?.percentPredicted != null ? ` • ${primaryReference.percentPredicted.toFixed(1)}% do previsto` : ''}
          </p>
          {comparison ? (
            <p className="mt-2 text-xs font-medium uppercase tracking-[0.16em] text-slate-400">
              Comparando com {comparison.label}
            </p>
          ) : null}
        </div>
        <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
          {stopLines.length ? `${stopLines.length} parada(s)` : 'Sem paradas'}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <SummaryMetric label="Corredor" value={`${attempt.corridorLength_m} m`} />
        <SummaryMetric label="Voltas" value={String(attempt.laps)} note={`${attempt.last_lap_meters} m após última volta`} />
        <SummaryMetric
          label="SpO2 mínima"
          value={
            (() => {
              const values = getTc6VitalsSeries(attempt)
                .map((item) => item.spo2)
                .filter((item): item is number => item != null);
              const minSpo2 = values.length ? Math.min(...values) : null;
              return minSpo2 != null ? `${minSpo2.toFixed(1)}%` : '—';
            })()
          }
          note={attempt.stop_report?.total_stopped_fmt ? `Parado por ${attempt.stop_report.total_stopped_fmt}` : null}
        />
      </div>

      {comparison?.attempt ? (
        <div className="rounded-[28px] border border-slate-100 bg-slate-50 p-4 shadow-sm">
          <div className="mb-3 text-xs font-black uppercase tracking-[0.2em] text-slate-400">
            Comparação consolidada
          </div>
          <TC6Comparison
            previous={comparison.attempt}
            current={attempt}
            previousDate={comparison.assessmentDate}
            currentDate={assessmentDate}
            previousReferenceKey={comparison.referenceKey}
            currentReferenceKey={selectedReferenceKey}
          />
        </div>
      ) : null}

      <div className="grid gap-4 xl:grid-cols-2">
        <div className="rounded-[24px] border border-slate-100 bg-slate-50 p-4">
          <div className="mb-3 text-xs font-black uppercase tracking-[0.2em] text-slate-400">Distância acumulada</div>
          {showStoredCharts && typeof distanceImage === 'string' && distanceImage.length > 0 ? (
            <img src={distanceImage} alt={`${attemptLabel} distância acumulada`} className="w-full rounded-2xl border border-slate-100 bg-white" />
          ) : (
            <TC6DistanceChartSurface
              attempt={attempt}
              referenceKey={selectedReferenceKey}
              comparisonAttempt={comparison?.attempt}
            />
          )}
        </div>

        <div className="rounded-[24px] border border-slate-100 bg-slate-50 p-4">
          <div className="mb-3 text-xs font-black uppercase tracking-[0.2em] text-slate-400">SpO2 e FC</div>
          {showStoredCharts && typeof vitalsImage === 'string' && vitalsImage.length > 0 ? (
            <img src={vitalsImage} alt={`${attemptLabel} SpO2 e FC`} className="w-full rounded-2xl border border-slate-100 bg-white" />
          ) : (
            <TC6VitalsChartSurface attempt={attempt} comparisonAttempt={comparison?.attempt} />
          )}
        </div>
      </div>

      <div className="overflow-hidden rounded-[24px] border border-slate-100 shadow-sm">
        <table className="w-full border-collapse text-left text-sm">
          <thead>
            <tr className="bg-slate-50 text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">
              <th className="px-5 py-4">Min</th>
              <th className="px-5 py-4">SpO2</th>
              <th className="px-5 py-4">FC</th>
              <th className="px-5 py-4">Dist. acum. (m)</th>
              <th className="px-5 py-4">Vel. (km/h)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {attempt.per_minute.map((entry, index) => (
              <tr key={`${attemptKey}-${entry.minute}`} className="hover:bg-slate-50/60">
                <td className="px-5 py-4 font-bold text-slate-700">{entry.minute}</td>
                <td className="px-5 py-4 text-slate-700">{entry.spo2 ?? '—'}</td>
                <td className="px-5 py-4 text-slate-700">{entry.hr ?? '—'}</td>
                <td className="px-5 py-4 font-semibold text-slate-900">{attempt.per_minute_distance_m?.[index] ?? '—'}</td>
                <td className="px-5 py-4 text-slate-700">
                  {typeof attempt.per_minute_speed_kmh?.[index] === 'number' ? attempt.per_minute_speed_kmh[index].toFixed(2) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <div className="rounded-[24px] border border-slate-100 bg-slate-50 p-5">
          <div className="text-xs font-black uppercase tracking-[0.2em] text-slate-400">Sinais vitais e Borg</div>
          <div className="mt-4 space-y-2 text-sm text-slate-700">
            <div className="flex items-center justify-between">
              <span>SpO2</span>
              <span className="font-semibold text-slate-900">
                {attempt.baseline.spo2 ?? '—'} → {attempt.final.spo2 ?? '—'}%
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span>FC</span>
              <span className="font-semibold text-slate-900">
                {attempt.baseline.hr ?? '—'} → {attempt.final.hr ?? '—'} bpm
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span>PA</span>
              <span className="font-semibold text-slate-900">
                {attempt.baseline.bp.sys ?? '—'}/{attempt.baseline.bp.dia ?? '—'} → {attempt.final.bp.sys ?? '—'}/{attempt.final.bp.dia ?? '—'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span>Borg dispneia</span>
              <span className="font-semibold text-slate-900">
                {attempt.baseline.borg.dyspnea ?? '—'} → {attempt.final.borg.dyspnea ?? '—'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span>Borg fadiga</span>
              <span className="font-semibold text-slate-900">
                {attempt.baseline.borg.fatigue ?? '—'} → {attempt.final.borg.fatigue ?? '—'}
              </span>
            </div>
          </div>
        </div>

        <div className="rounded-[24px] border border-slate-100 bg-slate-50 p-5">
          <div className="text-xs font-black uppercase tracking-[0.2em] text-slate-400">Paradas registradas</div>
          {stopLines.length === 0 ? (
            <div className="mt-4 text-sm text-slate-500">Nenhuma parada registrada nesta tentativa.</div>
          ) : (
            <div className="mt-4 space-y-3">
              {stopLines.map((interval, index) => (
                <div key={`${attemptKey}-stop-${index}`} className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
                  <div className="font-semibold text-slate-900">{interval.reason_label || 'Motivo não informado'}</div>
                  <div className="mt-1">
                    {interval.from_fmt} → {interval.to_fmt} ({interval.duration_fmt})
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export const resolveTc6ComparisonAttempt = (
  option: Tc6ComparisonOption | null,
  attemptKey: 'attempt1' | 'attempt2',
): Tc6ResolvedComparison | null => {
  if (!option?.payload) return null;

  const currentAttemptIndex = attemptKey === 'attempt2' ? 2 : 1;
  const directAttempt = currentAttemptIndex === 2 ? option.payload.attempt2 : option.payload.attempt1;
  if (directAttempt) {
    return {
      attempt: directAttempt,
      assessmentDate: option.assessmentDate,
      referenceKey: option.payload.referenceEquation,
      label:
        option.payload.attempt2 || currentAttemptIndex === 2
          ? `${option.label} • ${currentAttemptIndex}ª tentativa`
          : option.label,
    };
  }

  const fallback = getTc6BestAttempt(option.payload);
  if (!fallback.attempt) return null;

  return {
    attempt: fallback.attempt,
    assessmentDate: option.assessmentDate,
    referenceKey: option.payload.referenceEquation,
    label: `${option.label} • melhor tentativa`,
  };
};

export default function TC6ResultRenderer({
  payload,
  assessmentDate = null,
  chartImages = null,
  comparisonHistory = [],
  selectedComparisonId,
  onComparisonChange,
}: TC6ResultRendererProps) {
  const normalizedPayload = useMemo(() => recalculateTc6Payload(payload), [payload]);
  const summary = summarizeTc6Result(normalizedPayload);
  const attempts = getTc6Attempts(normalizedPayload);
  const { attempt: bestAttempt } = getTc6BestAttempt(normalizedPayload);
  const interpretation = resolveTC6Interpretation(normalizedPayload)
    || (bestAttempt
      ? `Melhor tentativa com ${bestAttempt.total_distance_m} m, referência ${summary.selectedReferenceLabel} e classificação ${summary.severity.toLowerCase()}.`
      : 'Sem interpretação automática disponível para este resultado.');
  const comparisonOptions = useMemo(
    () =>
      comparisonHistory
        .map((option) => ({
          ...option,
          payload: recalculateTc6Payload(option.payload),
        }))
        .filter((option) => getTc6Attempts(option.payload).length > 0),
    [comparisonHistory],
  );
  const [internalSelectedComparisonId, setInternalSelectedComparisonId] = useState<string>(TC6_NO_COMPARISON);
  const controlledComparison = typeof selectedComparisonId === 'string';
  const activeComparisonId = controlledComparison ? selectedComparisonId : internalSelectedComparisonId;
  const handleComparisonChange = onComparisonChange ?? setInternalSelectedComparisonId;

  useEffect(() => {
    if (controlledComparison) return;
    if (!comparisonOptions.length) {
      setInternalSelectedComparisonId(TC6_NO_COMPARISON);
      return;
    }

    setInternalSelectedComparisonId((current) =>
      current !== TC6_NO_COMPARISON && comparisonOptions.some((option) => option.id === current)
        ? current
        : comparisonOptions[0].id,
    );
  }, [comparisonOptions, controlledComparison]);

  const selectedComparison = useMemo(
    () => comparisonOptions.find((option) => option.id === activeComparisonId) ?? null,
    [activeComparisonId, comparisonOptions],
  );

  return (
    <div className="space-y-6">
      <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
        <SummaryMetric
          label="Melhor distância"
          value={summary.bestDistance_m != null ? `${summary.bestDistance_m} m` : '—'}
          note={summary.bestAttempt ? `${summary.bestAttempt}ª tentativa` : null}
        />
        <SummaryMetric
          label="Predito"
          value={summary.predictedDistance_m != null ? `${summary.predictedDistance_m.toFixed(1)} m` : '—'}
          note={summary.selectedReferenceLabel}
        />
        <SummaryMetric
          label="Percentual previsto"
          value={summary.percentPredicted != null ? `${summary.percentPredicted.toFixed(1)}%` : '—'}
          note={summary.severity}
        />
        <SummaryMetric
          label="Dessaturação"
          value={summary.minSpo2 != null ? `${summary.minSpo2.toFixed(1)}%` : '—'}
          note={summary.spo2Drop != null ? `Queda de ${summary.spo2Drop.toFixed(1)}%` : 'Sem queda significativa'}
        />
      </div>

      <div className="rounded-[28px] border border-slate-100 bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-400">Interpretação clínica do TC6</div>
            <h4 className="mt-2 text-2xl font-black tracking-tight text-slate-900">{summary.severity}</h4>
          </div>
          <div className={`rounded-full px-4 py-2 text-sm font-semibold ${getTC6SeverityTone(summary.percentPredicted)}`}>
            {summary.attemptCount === 2 ? '2 tentativas salvas' : '1 tentativa salva'}
          </div>
        </div>
        <div className="mt-4 whitespace-pre-wrap text-sm leading-relaxed text-slate-600">{interpretation}</div>
      </div>

      {comparisonOptions.length > 0 ? (
        <div className="rounded-[28px] border border-slate-100 bg-white p-6 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-400">Comparação histórica</div>
              <h4 className="mt-2 text-xl font-black tracking-tight text-slate-900">Overlay minuto a minuto</h4>
              <p className="mt-2 text-sm text-slate-500">
                Selecione uma avaliação anterior para sobrepor distância, SpO2 e FC. Quando a mesma tentativa não existir, usamos a melhor tentativa salva daquela avaliação.
              </p>
            </div>

            <div className="min-w-[280px] flex-1 md:max-w-md">
              <label className="block">
                <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Avaliação comparada</span>
                <select
                  value={activeComparisonId}
                  onChange={(event) => handleComparisonChange(event.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value={TC6_NO_COMPARISON}>Sem comparação</option>
                  {comparisonOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>
        </div>
      ) : null}

      {attempts.map((attempt, index) => (
        <AttemptCharts
          key={`attempt-${index + 1}`}
          attemptKey={index === 0 ? 'attempt1' : 'attempt2'}
          payload={normalizedPayload}
          chartImages={chartImages}
          attemptLabel={`TC6 - ${index + 1}ª tentativa`}
          assessmentDate={assessmentDate}
          comparison={resolveTc6ComparisonAttempt(selectedComparison, index === 0 ? 'attempt1' : 'attempt2')}
        />
      ))}
    </div>
  );
}
